import PropTypes from 'prop-types'
import settings from '@nobia/zeus-components/lib/settings'
import styled, { theme, prop } from '@nobia/zeus-components/lib/styled'
import { useAuth } from '@nobia/zeus-components/lib/auth'
import React, { useRef, useState, useContext, useEffect } from 'react'
import { gql, useMutation } from '@apollo/client'

import Checkbox from '../checkbox/Checkbox'
import { getInstallationText, getPrice } from '../cards/utils'
import { useTranslation } from '../../utils/useTranslation'
import { GUEST_ACCESS_EVENT, getGuestEmail } from '../../utils/guestAccess'
import { validateEmail } from '../../utils/validation'
import { hasMagnetSimplifiedVersion } from '../../brands/monthsToPay'
import Context from '../../app/context'
import { trackSubmit } from '../../utils/tracking'

const Wrapper = styled.div`
  border-radius: 3px;
  background-color: ${(p) => p.theme.email.bgColor};
  color: ${theme('colors.primary')};
  padding: 30px;
  min-width: 250px;
  max-width: 600px;
  margin: 0 auto;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    min-width: ${prop('theme.email.wrapper.minWidth')};
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    max-width: 500px;
    width: 63%;
  }
  @media (min-width: 1300px) {
    max-width: 600px;
    padding: ${prop('theme.email.padding')};
  }
`

const MagnetWrapper = styled.div`
  border-radius: 3px;
  background-color: ${(p) => p.theme.email.bgColor};
  color: ${theme('colors.primary')};
  min-width: 250px;
  max-width: 600px;
  margin: 0 auto;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    min-width: ${prop('theme.email.wrapper.minWidth')};
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    max-width: 500px;
    width: 63%;
  }
  @media (min-width: 1300px) {
    max-width: 600px;
  }
`

const MagnetInnerBlock = styled.div`
  border-radius: 3px;
  background-color: #eed7c4;
  color: ${theme('colors.primary')};
  padding: 30px;
  width: 100%;
  margin: 0 auto;
  margin-bottom: 10px;
`

const H3 = styled.h3`
  margin: 0 0 10px;
  color: ${theme('colors.primary')};
`
const Row = styled.div`
  display: flex;
  margin: 15px 0;
  cursor: pointer;
  min-height: ${prop('theme.email.minHeight')};
`

const MagnetRow = styled.div`
  display: flex;
  margin: 15px 0;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: ${prop('theme.email.minHeight')};
`

const P = styled.p`
  margin: 0;
  font-size: 16px;
`

const MagnetP = styled.p`
  margin: 0 0 0 0;
  width: 100%;
  padding-left: 15px;
  padding-right: 15px;
  font-size: 16px;
`

const Strong = styled.strong`
  word-break: break-word;
`
const Label = styled.label`
  height: 10px;
  margin: 0 0 0 10px;
  font-size: ${prop('theme.email.label.fontSize')};
  cursor: pointer;
  text-align: left;
  line-height: 26px;
`

const MagnetLabel = styled.label`
  margin: 0 0 0 0px;
  font-size: ${prop('theme.email.label.fontSize')};
  cursor: pointer;
  text-align: left;
  line-height: 26px;
`

const Error = styled(Label)`
  color: ${prop('theme.email.error.color')};
  display: block;
  text-align: center;
  margin-top: 10px;
  height: ${prop('theme.email.error.height')};
`
const InputWrapper = styled.div`
  display: grid;
  align-items: center;
  width: 100%;
  margin: 20px 0 0;
`

const MagnetInputWrapper = styled.div`
  display: grid;
  align-items: center;
  width: 90%;
  margin: 20px 0 0 29px;
`

const Input = styled.input`
  border-radius: 3px;
  border: ${theme('email.input.border')};
  padding: 15px;
  font-size: 16px;
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  color: ${theme('colors.primary')};
  background-color: ${theme('email.input.bgColor')};
`
const Placeholder = styled.label`
  height: 20px;
  grid-column: 1;
  grid-row: 1;
  color: ${theme('colors.darkGray')};
  font-size: 16px;
  margin-top: 2px;
  width: 100%;
  padding: 12px;
  pointer-events: none;
  z-index: 18;
  display: flex;
  align-items: center;
  justify-content: left;
`
const Button = styled.button`
  margin-top: 20px;
  padding: 15px;
  font-size: 16px;
  width: 100%;
  background-color: ${(p) => (p.disable ? '#DDDEDF' : p.theme.colors.button)};
  color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.white)};
  font-weight: ${prop('theme.button.fontWeight')};
  cursor: ${(p) => (p.disable ? 'not-allowed' : 'pointer')};
  transition: opacity 0.5s;
  border-radius: ${(p) => p.theme.button.borderRadius || '3px'};
  &:hover {
    background-color: ${(p) => (p.disable ? '#DDDEDF' : p.theme.colors.button)};
    opacity: 0.8;
    color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.white)};
  }
`

const MagnetButton = styled.button`
  margin-top: 20px;
  padding: 15px;
  font-size: 16px;
  width: ${(p) => (p.isDesktopWidth ? '45%' : '100%')};
  background-color: ${(p) => (p.disable ? '#DDDEDF' : p.theme.colors.button)};
  color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.white)};
  font-weight: ${prop('theme.button.fontWeight')};
  cursor: ${(p) => (p.disable ? 'not-allowed' : 'pointer')};
  transition: opacity 0.5s;
  border-radius: ${(p) => p.theme.button.borderRadius || '3px'};
  &:hover {
    background-color: ${(p) => (p.disable ? '#DDDEDF' : p.theme.colors.button)};
    opacity: 0.8;
    color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.white)};
  }
`

const OutlinedButton = styled(Button)`
  background-color: transparent;
  color: ${theme('colors.button')};
  border: 1px solid ${theme('colors.button')};
  &:hover {
    background-color: ${(p) => (p.disable ? '#DDDEDF' : 'transparent')};
    opacity: 0.8;
    color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.button)};
  }
`

const BadaButton = styled.a`
  display: block;
  text-decoration: none;
  margin-top: 20px;
  border-radius: 3px;
  padding: 15px;
  font-size: 16px;
  font-weight: 500;
  width: 100%;
  background-color: ${(p) => (p.disable ? '#DDDEDF' : p.theme.colors.button)};
  color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.white)};
  transition: opacity 0.5s;
  &:hover {
    opacity: 0.8;
  }
`

const CreatePriceConfigurationMutation = gql`
  mutation CreatePriceConfiguration($input: PriceConfigurationInput!) {
    priceConfiguration(input: $input) {
      emailAddress
    }
  }
`

const Email = ({ configuration, selection, financeTitle, months }) => {
  const { t } = useTranslation('app.components.email')
  const { t: badaT } = useTranslation('app.bada')
  const { profile } = useAuth()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [email, setEmail] = useState(
    () => profile?.username || getGuestEmail() || undefined
  )
  const [firstTime, setFirstTime] = useState(true)
  const [isValid, setIsValid] = useState(true)
  const [checked, setChecked] = useState(false)
  const [showPlaceholder, setShowPlaceholder] = useState(
    () => !(profile?.username || getGuestEmail())
  )
  const [createPriceConfiguration] = useMutation(
    CreatePriceConfigurationMutation
  )

  useEffect(() => {
    const onGuestAccess = () => {
      const guestEmail = getGuestEmail()
      if (!guestEmail) return
      setEmail((prev) => prev || guestEmail)
      setShowPlaceholder(false)
    }
    window.addEventListener(GUEST_ACCESS_EVENT, onGuestAccess)
    return () => window.removeEventListener(GUEST_ACCESS_EVENT, onGuestAccess)
  }, [])

  const { storage } = useContext(Context)
  const width = window.screen.width

  const isDesktopWidth = () => {
    if (width >= 1024) {
      return true
    }
    return false
  }

  const ref = useRef(null)
  const handleFocus = (e) => {
    setShowPlaceholder(false)
  }
  const handleBlur = (e) => {
    setShowPlaceholder(!e.target.value)
    setIsValid(validateEmail(e.target.value))
    setFirstTime(false)
  }
  const handleChange = (e) => {
    setEmail(e.target.value)
    if (firstTime) return
    setIsValid(validateEmail(e.target.value))
  }

  const isStorageSelected = () => {
    let noStorage = false
    for (let i = 0; i < storage.cards.length; i += 1) {
      if (storage.cards[i].amount > 0) {
        noStorage = true
        return noStorage
      }
    }
    return noStorage
  }

  const getStorageTitle = () => {
    let title = ''

    if (!isStorageSelected()) {
      title = 'No storage'
      return title
    }

    for (let i = 0; i < storage.cards.length; i += 1) {
      if (storage.cards[i].amount > 0) {
        title += `${storage.cards[i].title} ${storage.cards[i].amount}; `
      }
    }
    return title
  }

  const priceConfigurationPDF = {
    appliances: {
      title:
        configuration.appliances !== undefined
          ? configuration.appliances.title
          : null,
      id:
        configuration.appliances !== undefined
          ? configuration.appliances.id
          : null,
      price:
        configuration.appliances !== undefined
          ? getPrice(configuration.appliances, configuration)
          : 0,
    },
    installation: {
      title:
        configuration.installation !== undefined
          ? getInstallationText({
              card: configuration.installation,
              selectionItem: selection || {},
            })
          : null,
      id:
        configuration.installation !== undefined
          ? configuration.installation.id
          : null,
      price:
        configuration.installation !== undefined
          ? getPrice(configuration.installation, configuration)
          : 0,
    },
    range: {
      title: configuration.range.title,
      id: configuration.range.id,
      image: configuration.range.image,
    },
    ...(configuration.cabinet && {
      cabinet: {
        title: configuration.cabinet.title,
        id: configuration.cabinet.id,
      },
    }),
    unit: {
      title: configuration.units.title,
      id: configuration.units.id,
      price: getPrice(configuration.units, selection),
    },
    worktop: {
      title: configuration.worktops.title,
      id: configuration.worktops.id,
      price: getPrice(configuration.worktops, configuration),
    },
    flooring: {
      title:
        configuration.flooring !== undefined
          ? configuration.flooring.title
          : null,
      id:
        configuration.flooring !== undefined ? configuration.flooring.id : null,
      price:
        configuration.flooring !== undefined
          ? getPrice(configuration.flooring, configuration)
          : 0,
    },
    taps: {
      title: configuration.taps !== undefined ? configuration.taps.title : null,
      id: configuration.taps !== undefined ? configuration.taps.id : null,
      price:
        configuration.taps !== undefined
          ? getPrice(configuration.taps, configuration)
          : 0,
    },
    sinks: {
      title:
        configuration.sinks !== undefined ? configuration.sinks.title : null,
      id: configuration.sinks !== undefined ? configuration.sinks.id : null,
      price:
        configuration.sinks !== undefined
          ? getPrice(configuration.sinks, configuration)
          : 0,
    },
    storage: {
      title: configuration.storage !== undefined ? getStorageTitle() : null,
      id: configuration.storage !== undefined ? configuration.storage.id : null,
      price:
        configuration.storage !== undefined
          ? getPrice(configuration.storage, configuration, storage)
          : 0,
    },
    estimatedPrice: configuration.summary,
    estimatedPriceMonthly: configuration.summaryMonthly,
    paymentTerms: months.months,
    financeOption: financeTitle,
    emailAddress: email,
    optIn: checked,
  }

  const download = (blob) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = url
    a.download = 'price_configurator_quote.pdf'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const saveToDatabase = async () => {
    if (!isValid) {
      ref.current.focus()
      return false
    }
    if (!email) {
      setIsValid(false)
      ref.current.focus()
      return false
    }

    try {
      await createPriceConfiguration({
        variables: {
          input: {
            appliances: {
              title:
                configuration.appliances !== undefined
                  ? configuration.appliances.title
                  : null,
              id:
                configuration.appliances !== undefined
                  ? configuration.appliances.id
                  : null,
              price:
                configuration.appliances !== undefined
                  ? getPrice(configuration.appliances, configuration)
                  : 0,
            },
            installation: {
              title:
                configuration.installation !== undefined
                  ? getInstallationText({
                      card: configuration.installation,
                      selectionItem: selection || {},
                    })
                  : null,
              id:
                configuration.installation !== undefined
                  ? configuration.installation.id
                  : null,
              price:
                configuration.installation !== undefined
                  ? getPrice(configuration.installation, configuration)
                  : 0,
            },
            range: {
              title: configuration.range.title,
              id: configuration.range.id,
              image: configuration.range.image,
            },
            ...(configuration.cabinet && {
              cabinet: {
                title: configuration.cabinet.title,
                id: configuration.cabinet.id,
              },
            }),
            unit: {
              title: configuration.units.title,
              id: configuration.units.id,
              price: getPrice(configuration.units, selection),
            },
            worktop: {
              title: configuration.worktops.title,
              id: configuration.worktops.id,
              price: getPrice(configuration.worktops, configuration),
            },
            flooring: {
              title:
                configuration.flooring !== undefined
                  ? configuration.flooring.title
                  : null,
              id:
                configuration.flooring !== undefined
                  ? configuration.flooring.id
                  : null,
              price:
                configuration.flooring !== undefined
                  ? getPrice(configuration.flooring, configuration)
                  : 0,
            },
            taps: {
              title:
                configuration.taps !== undefined
                  ? configuration.taps.title
                  : null,
              id:
                configuration.taps !== undefined ? configuration.taps.id : null,
              price:
                configuration.taps !== undefined
                  ? getPrice(configuration.taps, configuration)
                  : 0,
            },
            sinks: {
              title:
                configuration.sinks !== undefined
                  ? configuration.sinks.title
                  : null,
              id:
                configuration.sinks !== undefined
                  ? configuration.sinks.id
                  : null,
              price:
                configuration.sinks !== undefined
                  ? getPrice(configuration.sinks, configuration)
                  : 0,
            },
            storage: {
              title:
                configuration.storage !== undefined ? getStorageTitle() : null,
              id:
                configuration.storage !== undefined
                  ? configuration.storage.id
                  : null,
              price:
                configuration.storage !== undefined
                  ? getPrice(configuration.storage, configuration, storage)
                  : 0,
            },
            estimatedPrice: configuration.summary,
            estimatedPriceMonthly: configuration.summaryMonthly,
            paymentTerms: months.months,
            financeOption: financeTitle,
            emailAddress: email,
            optIn: checked,
          },
        },
      })
      setShowConfirmation(true)
      return true
    } catch (error) {
      console.error(error)
    }
    return false
  }

  const handleSubmit = async () => {
    const saved = await saveToDatabase()
    if (!saved) {
      return
    }

    trackSubmit({
      email,
      buttonText: t('call-to-action'),
      newletter: checked,
      paymentOption: financeTitle,
      paymentTerm: months.months,
      quotePrice: configuration.summary,
    })
  }

  const handlePDFDownload = async () => {
    const saved = await saveToDatabase()
    if (!saved) {
      return
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(priceConfigurationPDF),
    }
    const url = `${settings.baseControllerUrl}/api/priceConfiguratorPDF`
    try {
      const response = await fetch(url, requestOptions)
      const isPDF = response.headers
        .get('content-type')
        ?.includes('application/pdf')
      if (isPDF) {
        const blob = await response.blob()
        download(blob)
        trackSubmit({
          email,
          buttonText: t('call-to-action-download'),
          newletter: checked,
          paymentOption: financeTitle,
          paymentTerm: months.months,
          quotePrice: configuration.summary,
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  /* eslint-disable */
  //a separate version for magnet website/big screen email block
  /* eslint-enable */
  if (hasMagnetSimplifiedVersion && isDesktopWidth()) {
    return (
      <MagnetWrapper>
        <MagnetInnerBlock>
          <H3>{t('book-a-meeting.title')}</H3>
          <MagnetP>{t('book-a-meeting.preamble')}</MagnetP>
          <BadaButton href={badaT('call-to-action.link')} target="_blank">
            {t('book-a-meeting.call-to-action_2')}
          </BadaButton>
        </MagnetInnerBlock>
        <H3>{t(showConfirmation ? 'sent.title' : 'title')}</H3>
        {showConfirmation ? (
          <P>
            {t('sent.description.pre-email')} <Strong>{email}</Strong>{' '}
            {t('sent.description.post-email')}
          </P>
        ) : (
          <MagnetP>{t('description')}</MagnetP>
        )}
        {!showConfirmation && (
          <>
            <MagnetInputWrapper>
              {showPlaceholder && <Placeholder>{t('placeholder')}</Placeholder>}
              <Input
                ref={ref}
                name="email"
                type="email"
                value={email}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </MagnetInputWrapper>
            {!isValid && <Error>{t('error')}</Error>}
            {!t('opt-in').startsWith('app') && (
              <MagnetRow onClick={() => setChecked(!checked)}>
                <Checkbox checked={checked} borderColor="#bbb" />
                <MagnetLabel>{t('opt-in')}</MagnetLabel>
              </MagnetRow>
            )}
            <MagnetInputWrapper>
              <MagnetRow>
                <MagnetButton onClick={handleSubmit} disable={!isValid}>
                  {t('call-to-action')}
                </MagnetButton>
                <MagnetButton onClick={handlePDFDownload} disable={!isValid}>
                  {t('call-to-action-download')}
                </MagnetButton>
              </MagnetRow>
            </MagnetInputWrapper>
          </>
        )}
        {showConfirmation && (
          <>
            <MagnetInputWrapper>
              <MagnetRow>
                <MagnetButton
                  onClick={() => {
                    setShowConfirmation(false)
                    setChecked(false)
                    setEmail(undefined)
                    setTimeout(() => {
                      ref.current.focus()
                    }, 200)
                  }}
                >
                  {t('sent.call-to-action')}
                </MagnetButton>
              </MagnetRow>
            </MagnetInputWrapper>
          </>
        )}
      </MagnetWrapper>
    )
  }

  /* eslint-disable */
  //email block version for all other brands and small devices
  /* eslint-enable */
  return (
    <Wrapper>
      <H3>{t(showConfirmation ? 'sent.title' : 'title')}</H3>
      {showConfirmation ? (
        <P>
          {t('sent.description.pre-email')} <Strong>{email}</Strong>{' '}
          {t('sent.description.post-email')}
        </P>
      ) : (
        <P>{t('description')}</P>
      )}
      {!showConfirmation && (
        <>
          <InputWrapper>
            {showPlaceholder && <Placeholder>{t('placeholder')}</Placeholder>}
            <Input
              ref={ref}
              name="email"
              type="email"
              onFocus={handleFocus}
              onBlur={handleBlur}
              value={email}
              onChange={handleChange}
            />
          </InputWrapper>
          {!isValid && <Error>{t('error')}</Error>}
          {!t('opt-in').startsWith('app') && (
            <Row onClick={() => setChecked(!checked)}>
              <Checkbox checked={checked} borderColor="#bbb" />
              <Label>{t('opt-in')}</Label>
            </Row>
          )}
          <Button onClick={handleSubmit} disable={!isValid}>
            {t('call-to-action')}
          </Button>
          {hasMagnetSimplifiedVersion && (
            <Button onClick={handlePDFDownload} disable={!isValid}>
              {t('call-to-action-download')}
            </Button>
          )}
        </>
      )}
      {showConfirmation && (
        <>
          <BadaButton href={badaT('call-to-action.link')} target="_blank">
            {t('book-a-meeting.call-to-action')}
          </BadaButton>
          <OutlinedButton
            onClick={() => {
              setShowConfirmation(false)
              setChecked(false)
              setEmail(undefined)
              setTimeout(() => {
                ref.current.focus()
              }, 200)
            }}
          >
            {t('sent.call-to-action')}
          </OutlinedButton>
        </>
      )}
    </Wrapper>
  )
}

Email.propTypes = {
  configuration: PropTypes.shape({
    appliances: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    installation: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    worktops: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    units: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    flooring: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    taps: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    sinks: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    storage: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
    range: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      image: PropTypes.number.isRequired,
    }),
    cabinet: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    }),
    summary: PropTypes.number,
    summaryMonthly: PropTypes.number,
    paymentTerms: PropTypes.number,
    financeOption: PropTypes.string,
  }).isRequired,
  selection: PropTypes.shape({}).isRequired,
  financeTitle: PropTypes.string.isRequired,
  months: PropTypes.shape({
    months: PropTypes.number.isRequired,
  }).isRequired,
}

export default Email
